import * as THREE from 'three';
import {
  CRAFT_BLOCK_TYPE,
  TEXTURE_GRID_SIZE,
  type BlockNoiseParams,
  type CustomBlockDefinition,
  type TexturePixels,
  textureGridSizeFromPixels,
} from '@shared/protocol';
import {
  applyNoiseToTexturePixels,
  isNoiseActive,
  resolveNoiseSeed,
} from '@shared/blockNoise';
import { blockTypeById } from '../world/blocks';
import { createCraftFaceMaterial } from '../world/coreTextures';
import {
  buildMicroVoxelGeometries,
  isEmptyGeometry,
  isVoxelOpaque,
} from '../world/MicroVoxelGeometry';

export type BlockPreviewSubject =
  | { kind: 'empty'; message?: string }
  | { kind: 'custom'; definition: CustomBlockDefinition }
  | { kind: 'solid'; color: string; opacity: number; glowIntensity?: number | null }
  | {
      kind: 'textured';
      pixels: TexturePixels;
      gridSize?: number;
      noise?: BlockNoiseParams | null;
      id?: number;
    }
  | { kind: 'builtin'; type: number };

const ROTATE_SPEED = 0.55;
const DRAG_SENSITIVITY = 0.008;
const MIN_PITCH = -1.2;
const MAX_PITCH = 1.2;

/** Pinned inventory preview: real Three.js mesh with slow auto-rotation. */
export class BlockPreview3D {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.PerspectiveCamera;
  private readonly pivot = new THREE.Group();
  private readonly content = new THREE.Group();
  private readonly note: HTMLParagraphElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly disposables: Array<{ dispose: () => void }> = [];
  private readonly onPointerDown = (event: PointerEvent) => this.handlePointerDown(event);
  private readonly onPointerMove = (event: PointerEvent) => this.handlePointerMove(event);
  private readonly onPointerUp = (event: PointerEvent) => this.handlePointerUp(event);
  private raf = 0;
  private lastFrame = 0;
  private disposed = false;
  private dragging = false;
  private dragPointerId: number | null = null;
  private lastPointerX = 0;
  private lastPointerY = 0;
  private yaw = 0;
  private pitch = 0.18;
  private resizeObserver: ResizeObserver | null = null;

  constructor(private readonly host: HTMLElement) {
    this.host.classList.add('detail-preview-3d');
    this.host.replaceChildren();
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'detail-preview-canvas';
    this.canvas.style.cursor = 'grab';
    this.canvas.title = 'Drag to rotate';
    this.note = document.createElement('p');
    this.note.className = 'detail-hint detail-preview-note';
    this.host.append(this.canvas, this.note);

    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: true,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.camera = new THREE.PerspectiveCamera(35, 1, 0.1, 20);
    this.camera.position.set(2.1, 1.7, 2.1);
    this.camera.lookAt(0, 0, 0);

    this.scene.add(new THREE.AmbientLight(0xffffff, 0.72));
    const key = new THREE.DirectionalLight(0xffffff, 0.95);
    key.position.set(2.5, 4, 1.5);
    this.scene.add(key);
    const fill = new THREE.DirectionalLight(0xffffff, 0.35);
    fill.position.set(-2, 1, -2);
    this.scene.add(fill);

    this.pivot.add(this.content);
    this.scene.add(this.pivot);
    this.applyOrbit();

    this.canvas.addEventListener('pointerdown', this.onPointerDown);
    this.canvas.addEventListener('pointermove', this.onPointerMove);
    this.canvas.addEventListener('pointerup', this.onPointerUp);
    this.canvas.addEventListener('pointercancel', this.onPointerUp);

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.host);
    this.resize();
    this.setSubject({ kind: 'empty' });
    this.lastFrame = performance.now();
    this.raf = requestAnimationFrame((time) => this.tick(time));
  }

  getOrbit(): { yaw: number; pitch: number } {
    return { yaw: this.yaw, pitch: this.pitch };
  }

  setOrbit(yaw: number, pitch: number): void {
    if (this.disposed) return;
    this.yaw = yaw;
    this.pitch = Math.max(MIN_PITCH, Math.min(MAX_PITCH, pitch));
    this.applyOrbit();
    this.renderFrame();
  }

  setSubject(subject: BlockPreviewSubject): void {
    if (this.disposed) return;
    this.clearContent();
    switch (subject.kind) {
      case 'empty':
        this.note.hidden = false;
        this.note.textContent = subject.message ?? 'No preview';
        this.canvas.style.opacity = '0.2';
        break;
      case 'custom':
        this.note.hidden = true;
        this.canvas.style.opacity = '1';
        this.mountCustom(subject.definition);
        break;
      case 'solid':
        this.note.hidden = true;
        this.canvas.style.opacity = '1';
        this.mountSolid(subject.color, subject.opacity, subject.glowIntensity ?? null);
        break;
      case 'textured':
        this.note.hidden = true;
        this.canvas.style.opacity = '1';
        this.mountTextured(
          subject.pixels,
          subject.gridSize,
          subject.noise,
          resolveNoiseSeed(subject.noise, subject.id ?? 0),
        );
        break;
      case 'builtin':
        this.note.hidden = true;
        this.canvas.style.opacity = '1';
        this.mountBuiltin(subject.type);
        break;
    }
    this.frameContent();
    this.renderFrame();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerup', this.onPointerUp);
    this.canvas.removeEventListener('pointercancel', this.onPointerUp);
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.clearContent();
    this.renderer.dispose();
    this.host.classList.remove('detail-preview-3d');
    this.host.replaceChildren();
  }

  private tick(time: number): void {
    if (this.disposed) return;
    const dt = Math.min(0.05, (time - this.lastFrame) / 1000);
    this.lastFrame = time;
    if (!this.dragging && this.content.children.length > 0) {
      this.yaw += dt * ROTATE_SPEED;
      this.applyOrbit();
    }
    this.renderFrame();
    this.raf = requestAnimationFrame((next) => this.tick(next));
  }

  private applyOrbit(): void {
    // Rotate the block in place; camera stays fixed and always looks at center.
    this.pivot.rotation.set(this.pitch, this.yaw, 0, 'YXZ');
  }

  private handlePointerDown(event: PointerEvent): void {
    if (this.disposed || event.button !== 0) return;
    if (this.content.children.length === 0) return;
    event.preventDefault();
    event.stopPropagation();
    this.dragging = true;
    this.dragPointerId = event.pointerId;
    this.lastPointerX = event.clientX;
    this.lastPointerY = event.clientY;
    this.canvas.style.cursor = 'grabbing';
    this.canvas.setPointerCapture(event.pointerId);
  }

  private handlePointerMove(event: PointerEvent): void {
    if (!this.dragging || event.pointerId !== this.dragPointerId) return;
    event.preventDefault();
    const dx = event.clientX - this.lastPointerX;
    const dy = event.clientY - this.lastPointerY;
    this.lastPointerX = event.clientX;
    this.lastPointerY = event.clientY;
    this.yaw += dx * DRAG_SENSITIVITY;
    this.pitch = Math.max(
      MIN_PITCH,
      Math.min(MAX_PITCH, this.pitch + dy * DRAG_SENSITIVITY),
    );
    this.applyOrbit();
    this.renderFrame();
  }

  private handlePointerUp(event: PointerEvent): void {
    if (!this.dragging || event.pointerId !== this.dragPointerId) return;
    this.dragging = false;
    this.dragPointerId = null;
    this.canvas.style.cursor = 'grab';
    try {
      this.canvas.releasePointerCapture(event.pointerId);
    } catch {
      /* already released */
    }
  }

  private renderFrame(): void {
    this.renderer.render(this.scene, this.camera);
  }

  private resize(): void {
    const width = Math.max(1, this.host.clientWidth);
    const height = Math.max(1, this.host.clientHeight);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
    this.renderFrame();
  }

  private clearContent(): void {
    while (this.content.children.length > 0) {
      this.content.remove(this.content.children[0]!);
    }
    for (const item of this.disposables) {
      if (item instanceof THREE.Material) {
        const map = (item as THREE.MeshLambertMaterial).map;
        map?.dispose();
      }
      item.dispose();
    }
    this.disposables.length = 0;
  }

  private track<T extends { dispose: () => void }>(item: T): T {
    this.disposables.push(item);
    return item;
  }

  private mountSolid(
    color: string,
    opacity: number,
    glowIntensity: number | null = null,
  ): void {
    const geometry = this.track(new THREE.BoxGeometry(1, 1, 1));
    const glowing = (glowIntensity ?? 0) > 0;
    const material = this.track(
      glowing
        ? new THREE.MeshBasicMaterial({
          color,
          opacity,
          transparent: opacity < 1,
          depthWrite: opacity >= 1,
          toneMapped: false,
        })
        : new THREE.MeshLambertMaterial({
          color,
          opacity,
          transparent: opacity < 1,
          depthWrite: opacity >= 1,
        }),
    );
    const mesh = new THREE.Mesh(geometry, material);
    this.content.add(mesh);
    if (glowing) {
      const light = this.track(new THREE.PointLight(
        color,
        Math.min(1, glowIntensity!) * 1.25,
        4,
        2,
      ));
      light.castShadow = false;
      light.position.set(0, 0, 0);
      this.content.add(light);
    }
  }

  private mountBuiltin(type: number): void {
    if (type === CRAFT_BLOCK_TYPE) {
      const geometry = this.track(new THREE.BoxGeometry(1, 1, 1));
      const materials = [
        this.track(createCraftFaceMaterial('right')),
        this.track(createCraftFaceMaterial('left')),
        this.track(createCraftFaceMaterial('top')),
        this.track(createCraftFaceMaterial('bottom')),
        this.track(createCraftFaceMaterial('front')),
        this.track(createCraftFaceMaterial('back')),
      ];
      this.content.add(new THREE.Mesh(geometry, materials));
      return;
    }
    const block = blockTypeById(type);
    this.mountSolid(block.css, 1);
  }

  private mountTextured(
    pixels: TexturePixels,
    gridSize?: number,
    noise: BlockNoiseParams | null = null,
    seed = 0,
  ): void {
    const size = textureGridSizeFromPixels(pixels) ?? gridSize ?? TEXTURE_GRID_SIZE;
    const faces = isNoiseActive(noise)
      ? applyNoiseToTexturePixels(pixels, noise, seed)
      : pixels;
    const geometry = this.track(new THREE.BoxGeometry(1, 1, 1));
    const materials = faces.map((face) => {
      const data = new Uint8Array(size * size * 4);
      face.forEach((color, index) => {
        const [r, g, b, a] = rgbaBytes(color);
        data[index * 4] = r;
        data[index * 4 + 1] = g;
        data[index * 4 + 2] = b;
        data[index * 4 + 3] = a;
      });
      const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.magFilter = THREE.NearestFilter;
      texture.minFilter = THREE.NearestFilter;
      texture.generateMipmaps = false;
      texture.needsUpdate = true;
      const transparent = face.some((color) => rgbaBytes(color)[3] < 255);
      return this.track(new THREE.MeshLambertMaterial({
        map: texture,
        transparent,
        depthWrite: !transparent,
      }));
    });
    this.content.add(new THREE.Mesh(geometry, materials));
  }

  private mountCustom(definition: CustomBlockDefinition): void {
    if (definition.voxels.length === 0) {
      this.note.hidden = false;
      this.note.textContent = 'Upload a .bfcb geometry file to preview this block.';
      this.canvas.style.opacity = '0.2';
      return;
    }
    const geometries = buildMicroVoxelGeometries(
      definition.voxels,
      definition.gridSize,
      definition.noise,
      resolveNoiseSeed(definition.noise, definition.id),
    );
    this.track(geometries.opaque);
    this.track(geometries.transparent);
    this.track(geometries.glowing);
    const opaqueMat = this.track(new THREE.MeshLambertMaterial({
      vertexColors: true,
    }));
    const transparentMat = this.track(new THREE.MeshLambertMaterial({
      vertexColors: true,
      transparent: true,
      depthWrite: false,
    }));
    const glowingMat = this.track(new THREE.MeshBasicMaterial({
      vertexColors: true,
      toneMapped: false,
    }));
    const group = new THREE.Group();
    if (!isEmptyGeometry(geometries.opaque)) {
      const mesh = new THREE.Mesh(geometries.opaque, opaqueMat);
      mesh.castShadow = definition.voxels.some(([, material]) => isVoxelOpaque(material));
      group.add(mesh);
    }
    if (!isEmptyGeometry(geometries.transparent)) {
      const mesh = new THREE.Mesh(geometries.transparent, transparentMat);
      mesh.renderOrder = 1;
      group.add(mesh);
    }
    if (!isEmptyGeometry(geometries.glowing)) {
      const mesh = new THREE.Mesh(geometries.glowing, glowingMat);
      mesh.renderOrder = 2;
      group.add(mesh);
    }
    // Micro-voxel geometry is in 0..1 cube space already.
    group.position.set(-0.5, -0.5, -0.5);
    this.content.add(group);
  }

  private frameContent(): void {
    // Keep a unit-cube framing; custom meshes already sit in 0..1 after offset.
    this.content.position.set(0, 0, 0);
  }
}

function rgbaBytes(color: string): [number, number, number, number] {
  const hex = color.slice(1);
  const value = Number.parseInt(hex, 16);
  return hex.length === 8
    ? [(value >>> 24) & 255, (value >>> 16) & 255, (value >>> 8) & 255, value & 255]
    : [(value >> 16) & 255, (value >> 8) & 255, value & 255, 255];
}
